<template>
  <UiBaseButtonBase
    v-bind="{ ...$props, ...$attrs }"
    :class="[`is-${size}`]">
    <template
      v-for="(_, name) in $slots"
      :key="name"
      #[name]="slotData">
      <slot
        :name="name"
        v-bind="slotData ?? {}" />
    </template>
  </UiBaseButtonBase>
</template>

<script lang="ts">
import type { PropType } from 'vue'

export default {
  name: 'UiBaseButton',
  inheritAttrs: false,
  props: {
    text: { type: String, default: undefined },
    type: { type: String as PropType<'button' | 'submit'>, default: undefined },
    theme: {
      type: String as PropType<'primary' | 'neutral' | 'text' | 'alert'>,
      default: undefined,
    },
    // App-specific extension — the package's BaseButton has no size prop.
    // md (48px) is the lab-customer default per components/button.md.
    size: {
      type: String as PropType<'sm' | 'md' | 'lg'>,
      default: 'md',
    },
    spacingBottom: {
      type: String as PropType<'none' | 'small'>,
      default: undefined,
    },
    isFullWidth: { type: Boolean, default: undefined },
    isLoading: { type: Boolean, default: undefined },
    isDisabled: { type: Boolean, default: undefined },
    showSaveIcon: { type: Boolean, default: undefined },
  },
}
</script>

<style>
@reference "../../globals/css/base.css";

.s-base-button {
  @apply gap-2;
  min-width: 0;
  border-radius: var(--button-radius);
  font-family: var(--button-font);
  font-weight: var(--button-font-weight);
  font-size: var(--button-font-size);
  border: var(--button-border-width) solid transparent;

  &.theme-primary {
    @apply bg-action text-text-on-action;

    &:hover {
      @apply bg-action-hover;
    }

    &:active {
      @apply bg-action-pressed;
    }
  }

  &.theme-neutral {
    @apply bg-transparent text-action border-action;

    &:hover {
      @apply bg-action-soft-bg;
    }
  }

  &.theme-text {
    @apply bg-transparent text-text-secondary;

    &:hover {
      @apply bg-surface-sunken text-text-primary;
    }
  }

  &.theme-alert {
    @apply bg-transparent text-feedback-error border-feedback-error;

    &:hover {
      @apply bg-feedback-error-bg;
    }
  }

  &.is-sm {
    height: var(--button-height-sm);
    padding: 0 var(--button-padding-x-sm);
    font-size: 14px;
  }

  &.is-md {
    height: var(--button-height-md);
    padding: 0 var(--button-padding-x-md);
  }

  &.is-lg {
    height: var(--button-height-lg);
    padding: 0 var(--button-padding-x-md);
    font-size: 16px;
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
  }
}
</style>
